import { HIDDEN_PRICE } from '../../constants';
import {
  ICarouselProduct,
  IFinance,
  IProduct,
  ISearchProduct,
} from '@monofront/types';
import {
  format,
  getFormattedProductWeight,
  getFormattedValues,
  getHasEarlyExpiration,
  getIsAntiWaste,
  getIsFresh,
  getIsOriginFranceFromTags,
  getIsUltraFresh,
  getMaxQuantity,
  getRecupTagFromTags,
  getReviewsGlobal,
  getTagValues,
} from '../../utils';
import { useProductCustomer } from '../../contexts/ProductCustomerContext';
import { useProductPricing } from './useProductPricing';

export interface IRemoveProductCard {
  title: string;
  variantId: string;
  productId: string;
  vendor: string;
  sku: string;
  quantity: number;
  price: string;
  grams: string;
  productImages: null;
}

export interface ICardProductInfos {
  /** flag to determine if this is fresh product */
  isFresh: boolean;
  /** flag to determine if this is ultra fresh product */
  isUltraFresh: boolean;
  /** flag to determine if this is a product with early expiration */
  hasEarlyExpiration: boolean;
  /** flag to determine if the card should use the anti waste display */
  isAntiWaste: boolean;
  /** the crossed out price */
  comparedPrice?: number;
  /** the discount in percent */
  discountPercent?: number;
  /** the crossed out price ready to be displayed */
  formattedComparedPrice?: string;
  /** the price ready to be displayed */
  formattedPrice?: string;
  /** the product weight to be displayed */
  formattedProductWeight?: {
    formattedWeight: string;
    drainedWeightTranslationKey: string;
  };
  /** the unit price ready to be displayed */
  formattedUnitPrice?: string;
  /** the values filtered */
  formattedValues?: string[];
  /** product handle in prismic */
  handle: string;
  /** id of the product needed for the add to cart */
  id: number;
  /** the image to display in the card */
  img?: string;
  /** the image to display in the card */
  image?: string;
  /** the quantity that is left after the user adds to cart */
  inventoryQuantity: number;
  /** the max quantity that the user can add to cart (min value between the max quantity defined in the PIM and the inventory quantity) */
  maxQuantity: number;
  /** product price value */
  price: number;
  /** category of the product needed to add to cart */
  productType: string;
  /** anti waste tag to display */
  antiWasteTag?: {
    translationKey: string;
    date?: string;
  };
  /** flag to indicate that the price should be hidden */
  shouldHideProductPrice: boolean;
  /** sky of the product, needed to add to wishlist and to cart */
  sku: string;
  /** the product tags */
  tags: string[];
  /** the title of the card */
  title: string;
  /** the unit of measurement of the product */
  unitOfMeasurement?: string;
  /** id of the product or the variant displayed if it is a variant */
  variantId: string;
  /** vendor name */
  vendor: string;
  /** algolia product id */
  objectID: string;
  /** all unit of measurement of the product */
  finance?: IFinance;
  /** flag to determine if the product is from France */
  isOriginFrance: boolean;
}

/** type of the useCardInfo hook */
export type IUseCardInfo = (
  product: ISearchProduct | ICarouselProduct,
) => ICardProductInfos;

/**
 * This hook is meant to calculate all the informations needed in the card products of lists and carousels
 * @param product The product to display in the card
 * @returns IUseCardInfo
 */
export const useCardProductInfos = (
  product: IProduct | ISearchProduct | IRemoveProductCard,
  quantity: number,
) => {
  const { isAuthenticated } = useProductCustomer();
  const pricing =
    'price' in product &&
    'tags' in product &&
    'compare_at_price' in product &&
    'meta' in product
      ? useProductPricing(product, quantity)
      : undefined;

  const priceValue = pricing?.price ?? undefined;
  const comparedPrice = pricing?.compareAtPrice;
  const discountPercent = pricing
    ? Number(pricing.discountPercent).toFixed(0)
    : undefined;

  const shouldHideProductPrice =
    !isAuthenticated &&
    'tags' in product &&
    product.tags?.some((tag) => tag === HIDDEN_PRICE.STARTWITH);

  const maxQuantity =
    'inventory_quantity' in product
      ? getMaxQuantity(product)
      : product.quantity;
  const tagsValues = 'tags' in product ? getTagValues(product.tags) : [];
  const formattedValues = getFormattedValues(tagsValues);
  const formattedDiets = getFormattedValues(tagsValues);
  const formattedGermanValues = getFormattedValues(tagsValues);
  const isAntiWaste = 'tags' in product ? getIsAntiWaste(product.tags) : false;
  const isFresh = 'tags' in product ? getIsFresh(product.tags) : false;
  const isUltraFresh =
    'tags' in product ? getIsUltraFresh(product.tags) : false;
  const hasEarlyExpiration =
    'tags' in product ? getHasEarlyExpiration(product.tags) : false;
  const antiWasteTag = isAntiWaste
    ? 'tags' in product
      ? getRecupTagFromTags(product.tags)
      : undefined
    : undefined;
  const formattedProductWeight =
    'meta' in product
      ? getFormattedProductWeight(product.meta?.finance)
      : undefined;
  const isOriginFrance =
    'tags' in product ? getIsOriginFranceFromTags(product.tags) : false;
  const img =
    'image' in product
      ? product.image ||
        ('product_image' in product ? product.product_image : undefined)
      : undefined;
  const reviews = getReviewsGlobal(product as ISearchProduct) || {
    average_rating: undefined,
    count: undefined,
  };
  const { average_rating, count } = reviews;

  return {
    isAntiWaste,
    isFresh,
    isUltraFresh,
    hasEarlyExpiration,
    comparedPrice,
    discountPercent,
    formattedComparedPrice: comparedPrice ? format(comparedPrice) : undefined,
    formattedPrice: priceValue ? format(priceValue) : undefined,
    formattedProductWeight,
    formattedUnitPrice:
      'meta' in product && product.meta.finance?.unit_price
        ? format(product.meta.finance.unit_price)
        : undefined,
    formattedValues: [
      ...formattedValues,
      ...formattedDiets,
      ...formattedGermanValues,
    ],
    handle:
      (product as ICarouselProduct).slug ||
      (product as ISearchProduct).handle ||
      '',
    id: 'id' in product ? product.id : parseInt(product.productId, 10),
    img: img,
    image: img,
    inventoryQuantity:
      'inventory_quantity' in product
        ? product.inventory_quantity
        : product.quantity,
    maxQuantity,
    price: priceValue || 0,
    productType: 'product_type' in product ? product.product_type : '',
    antiWasteTag,
    shouldHideProductPrice,
    sku: product.sku,
    tags: 'tags' in product ? product.tags : [],
    title: product.title,
    unitOfMeasurement:
      'meta' in product ? product.meta.finance?.unit_of_measurement : undefined,
    variantId: 'variantId' in product ? product.variantId : product.objectID,
    vendor: product.vendor,
    objectID: 'objectID' in product ? product.objectID : '',
    finance: 'meta' in product ? product.meta?.finance : undefined,
    isOriginFrance,
    isUnpublished: img === undefined,
    averageRating: average_rating || undefined,
    reviewsCount: count || undefined,
  };
};
